import React from "react";
import {
  Box,
  Typography,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  styled,
  IconButton,
  Popover,
  Chip,
  Button,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FormatService from "../../services/formatService";
import { Actions, Subjects } from "../../permission/constants";
import { Collection } from "../../entities/Collection";

export interface StatElementProps {
  title: string;
  currencySymbol: string;
  value?: number;
  action: Actions;
  subject: Subjects | Collection;
  help?: React.JSX.Element;
  isPremium?: boolean;
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 350,
    fontSize: "5px !important",
    border: "1px solid #dadde9",
    padding: "14px",
  },
}));

const StatElement = ({
  title,
  currencySymbol,
  value,
  action,
  subject,
  help,
  isPremium,
}: StatElementProps) => {
  const { getMoneyFormat } = FormatService();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "help-popover" : undefined;

  return (
    <Box
      className="shadow-box"
      sx={{
        padding: "20px",
        borderRadius: "10px",
        backgroundColor: "white",
        marginBottom: "1rem",
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="statTitle">{title}</Typography>
          {help && (
            <>
              <IconButton
                onClick={handleClick}
                sx={{ padding: 0, marginLeft: "10px" }}
              >
                <HelpOutlineIcon />
              </IconButton>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ width: "300px", padding: "0.8rem 0.7rem" }}>
                  {help}
                </Box>
              </Popover>
            </>
          )}
          {title === "Current value" && (
            <Chip
              label="BETA"
              sx={{
                fontStyle: "italic",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: "lightgrey",
                marginLeft: "auto",
                height: "24px",
                position: "relative",
                bottom: 20,
                left: 16,
              }}
            />
          )}
        </Box>

        {!isPremium && action !== Actions.VIEW_PURCHASING_VALUE && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              marginBottom: "5px",
            }}
          >
            <Typography>First 10 shirts only</Typography>
            <Button
              sx={{
                background: "linear-gradient(60deg, #e6c305 0%, #f4ee32 100%)",
                color: "black",
                paddingY: "2px",
                fontWeight: 600,
              }}
              onClick={() => (window.location.href = "/upgrade-membership")}
            >
              Upgrade
            </Button>
          </Box>
        )}
      </Box>
      <Typography
        variant="statNumber"
        sx={{ fontStyle: "italic", fontSize: "2rem" }}
      >
        {getMoneyFormat(value)}
      </Typography>
    </Box>
  );
};

export default StatElement;
